import { FilterOutlined } from "@ant-design/icons";
import {
  Button,
  Input,
  Modal,
  Table,
  Tabs,
  Typography,
  Image,
  Space,
  Tag,
  Row,
} from "antd";
import { useEffect, useState, useMemo } from "react";
import { getPhotoUrl, getSlug } from "utils/common";
import restaurantApi from "../api";

const { Paragraph } = Typography;

function FilterRestaurantModal({ open, onCancel }) {
  const [activeTab, setActiveTab] = useState("email");
  const [allEmailData, setAllEmailData] = useState([]);
  const [allIdentityData, setAllIdentityData] = useState([]);
  const [allContractData, setAllContractData] = useState([]);
  const [emailLoading, setEmailLoading] = useState(false);
  const [identityLoading, setIdentityLoading] = useState(false);
  const [contractLoading, setContractLoading] = useState(false);
  const [emailSearch, setEmailSearch] = useState("");
  const [identitySearch, setIdentitySearch] = useState("");
  const [contractSearch, setContractSearch] = useState("");
  const [emailPagination, setEmailPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [identityPagination, setIdentityPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [contractPagination, setContractPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [emailFetched, setEmailFetched] = useState(false);
  const [identityFetched, setIdentityFetched] = useState(false);
  const [contractFetched, setContractFetched] = useState(false);

  useEffect(() => {
    if (open) {
      // Reset states when modal opens
      setActiveTab("email");
      setEmailSearch("");
      setIdentitySearch("");
      setContractSearch("");
      setAllEmailData([]);
      setAllIdentityData([]);
      setAllContractData([]);
      setEmailPagination({ current: 1, pageSize: 10, total: 0 });
      setIdentityPagination({ current: 1, pageSize: 10, total: 0 });
      setContractPagination({ current: 1, pageSize: 10, total: 0 });
      setEmailFetched(false);
      setIdentityFetched(false);
      setContractFetched(false);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      if (activeTab === "email" && !emailFetched) {
        fetchEmailData();
      } else if (activeTab === "identity" && !identityFetched) {
        fetchIdentityData();
      } else if (activeTab === "contract" && !contractFetched) {
        fetchContractData();
      }
    }
  }, [activeTab, open, emailFetched, identityFetched, contractFetched]);

  const fetchEmailData = async () => {
    setEmailLoading(true);
    try {
      const response = await restaurantApi.getNoConfirmEmail({});
      if (response.data) {
        setAllEmailData(response.data);
        setEmailPagination((prev) => ({
          ...prev,
          current: 1,
          total: response.data.length,
        }));
        setEmailFetched(true);
      }
    } catch (error) {
      console.error("Error fetching email data:", error);
    } finally {
      setEmailLoading(false);
    }
  };

  const fetchIdentityData = async () => {
    setIdentityLoading(true);
    try {
      const response = await restaurantApi.getNoIdentity({});
      if (response.data) {
        setAllIdentityData(response.data);
        setIdentityPagination((prev) => ({
          ...prev,
          current: 1,
          total: response.data.length,
        }));
        setIdentityFetched(true);
      }
    } catch (error) {
      console.error("Error fetching identity data:", error);
    } finally {
      setIdentityLoading(false);
    }
  };

  const fetchContractData = async () => {
    setContractLoading(true);
    try {
      const response = await restaurantApi.getNoContract({});
      if (response.data) {
        setAllContractData(response.data);
        setContractPagination((prev) => ({
          ...prev,
          current: 1,
          total: response.data.length,
        }));
        setContractFetched(true);
      }
    } catch (error) {
      console.error("Error fetching contract data:", error);
    } finally {
      setContractLoading(false);
    }
  };

  const onEmailSearch = (value) => {
    setEmailSearch(value);
    setEmailPagination((prev) => ({ ...prev, current: 1 }));
  };

  const onEmailSearchChange = (e) => {
    const value = e.target.value;
    setEmailSearch(value);
    setEmailPagination((prev) => ({ ...prev, current: 1 }));
  };

  const onEmailTableChange = (pagination) => {
    setEmailPagination((prev) => ({
      ...prev,
      current: pagination.current,
      pageSize: pagination.pageSize,
    }));
  };

  const onIdentitySearch = (value) => {
    setIdentitySearch(value);
    setIdentityPagination((prev) => ({ ...prev, current: 1 }));
  };

  const onIdentitySearchChange = (e) => {
    const value = e.target.value;
    setIdentitySearch(value);
    setIdentityPagination((prev) => ({ ...prev, current: 1 }));
  };

  const onIdentityTableChange = (pagination) => {
    setIdentityPagination((prev) => ({
      ...prev,
      current: pagination.current,
      pageSize: pagination.pageSize,
    }));
  };

  const onContractSearch = (value) => {
    setContractSearch(value);
    setContractPagination((prev) => ({ ...prev, current: 1 }));
  };

  const onContractSearchChange = (e) => {
    const value = e.target.value;
    setContractSearch(value);
    setContractPagination((prev) => ({ ...prev, current: 1 }));
  };

  const onContractTableChange = (pagination) => {
    setContractPagination((prev) => ({
      ...prev,
      current: pagination.current,
      pageSize: pagination.pageSize,
    }));
  };

  const getColumns = (pagination) => [
    {
      title: "STT",
      align: "center",
      width: "5%",
      render: (text, record, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: "Hình ảnh",
      width: "10%",
      dataIndex: "avatar",
      render: (avatar) =>
        avatar ? (
          <Image
            alt="Restaurant"
            src={getPhotoUrl(avatar?.path || avatar)}
            width="60px"
            height="60px"
          />
        ) : null,
    },
    {
      title: "Tên quán",
      width: "30%",
      dataIndex: "name",
      render: (name, record) => (
        <Space>
          <Paragraph style={{ margin: 0 }}>{name || "N/A"}</Paragraph>
          <Paragraph
            copyable={{
              text: record._id,
              tooltips: ["Sao chép ID", "Đã sao chép!"],
            }}
            style={{ margin: 0 }}
          />
        </Space>
      ),
    },
    {
      title: "Số điện thoại",
      width: "15%",
      dataIndex: "phone",
      render: (phone) => phone || "N/A",
    },
    {
      title: "Trạng thái",
      width: "15%",
      dataIndex: "emailVerified",
      render: (emailVerified) =>
        emailVerified ? (
          <Tag color="green">Đã xác thực</Tag>
        ) : (
          <Tag color="red">Chưa xác thực</Tag>
        ),
    },
    {
      title: "Trạng thái",
      width: "15%",
      dataIndex: "identityStatus",
      render: (status) => {
        if (!status) return <Tag>N/A</Tag>;
        const statusMap = {
          none: { text: "Chưa có", color: "default" },
          pending: { text: "Chờ xử lý", color: "orange" },
          rejected: { text: "Đã từ chối", color: "red" },
        };
        const statusInfo = statusMap[status] || {
          text: status,
          color: "default",
        };
        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
      },
    },
    {
      title: "Trạng thái",
      width: "15%",
      dataIndex: "contractStatus",
      render: (status) => {
        if (!status) return <Tag>N/A</Tag>;
        const statusMap = {
          none: { text: "Chưa có", color: "default" },
          draft: { text: "Bản nháp", color: "default" },
          pending: { text: "Chờ xử lý", color: "orange" },
          rejected: { text: "Đã từ chối", color: "red" },
        };
        const statusInfo = statusMap[status] || {
          text: status,
          color: "default",
        };
        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
      },
    },
  ];

  // Filter data by slug
  const filterDataBySlug = (allData, searchKeyword) => {
    if (!searchKeyword || !searchKeyword.trim()) {
      return allData;
    }
    const searchSlug = getSlug(searchKeyword);
    return allData.filter((item) => {
      const itemSlug = item.slug?.toLowerCase() || "";
      return itemSlug.includes(searchSlug);
    });
  };

  // Get filtered and paginated data for display
  const getFilteredAndPaginatedData = (allData, searchKeyword, pagination) => {
    const filteredData = filterDataBySlug(allData, searchKeyword);
    const start = (pagination.current - 1) * pagination.pageSize;
    const end = start + pagination.pageSize;
    return {
      data: filteredData.slice(start, end),
      total: filteredData.length,
    };
  };

  const emailResult = useMemo(
    () =>
      getFilteredAndPaginatedData(allEmailData, emailSearch, emailPagination),
    [
      allEmailData,
      emailSearch,
      emailPagination.current,
      emailPagination.pageSize,
    ]
  );
  const identityResult = useMemo(
    () =>
      getFilteredAndPaginatedData(
        allIdentityData,
        identitySearch,
        identityPagination
      ),
    [
      allIdentityData,
      identitySearch,
      identityPagination.current,
      identityPagination.pageSize,
    ]
  );
  const contractResult = useMemo(
    () =>
      getFilteredAndPaginatedData(
        allContractData,
        contractSearch,
        contractPagination
      ),
    [
      allContractData,
      contractSearch,
      contractPagination.current,
      contractPagination.pageSize,
    ]
  );

  const emailData = emailResult.data;
  const identityData = identityResult.data;
  const contractData = contractResult.data;

  // Create pagination objects with correct totals
  const emailPaginationWithTotal = {
    ...emailPagination,
    total: emailResult.total,
  };
  const identityPaginationWithTotal = {
    ...identityPagination,
    total: identityResult.total,
  };
  const contractPaginationWithTotal = {
    ...contractPagination,
    total: contractResult.total,
  };

  const emailColumns = getColumns(emailPagination).filter(
    (col) =>
      col.dataIndex !== "identityStatus" && col.dataIndex !== "contractStatus"
  );
  const identityColumns = getColumns(identityPagination).filter(
    (col) =>
      col.dataIndex !== "emailVerified" && col.dataIndex !== "contractStatus"
  );
  const contractColumns = getColumns(contractPagination).filter(
    (col) =>
      col.dataIndex !== "emailVerified" && col.dataIndex !== "identityStatus"
  );

  const items = [
    {
      key: "email",
      label: "Chưa xác thực email",
      children: (
        <div>
          <Row
            justify="space-between"
            align="middle"
            style={{ marginBottom: 16 }}
          >
            <Typography.Text strong>
              Tổng cộng [{emailResult.total}] quán chưa xác thực email
            </Typography.Text>
            <Input.Search
              placeholder="Tìm theo tên quán"
              onSearch={onEmailSearch}
              onChange={onEmailSearchChange}
              value={emailSearch}
              enterButton
              allowClear
              style={{ width: 300 }}
            />
          </Row>
          <Table
            bordered
            loading={emailLoading}
            columns={emailColumns}
            dataSource={emailData}
            rowKey={(record) => record._id}
            pagination={emailPaginationWithTotal}
            onChange={onEmailTableChange}
          />
        </div>
      ),
    },
    {
      key: "identity",
      label: "Chưa xác thực CCCD",
      children: (
        <div>
          <Row
            justify="space-between"
            align="middle"
            style={{ marginBottom: 16 }}
          >
            <Typography.Text strong>
              Tổng cộng [{identityResult.total}] quán chưa xác thực CCCD
            </Typography.Text>
            <Input.Search
              placeholder="Tìm theo tên quán"
              onSearch={onIdentitySearch}
              onChange={onIdentitySearchChange}
              value={identitySearch}
              enterButton
              allowClear
              style={{ width: 300 }}
            />
          </Row>
          <Table
            bordered
            loading={identityLoading}
            columns={identityColumns}
            dataSource={identityData}
            rowKey={(record) => record._id}
            pagination={identityPaginationWithTotal}
            onChange={onIdentityTableChange}
          />
        </div>
      ),
    },
    {
      key: "contract",
      label: "Chưa ký hợp đồng",
      children: (
        <div>
          <Row
            justify="space-between"
            align="middle"
            style={{ marginBottom: 16 }}
          >
            <Typography.Text strong>
              Tổng cộng [{contractResult.total}] quán chưa ký hợp đồng
            </Typography.Text>
            <Input.Search
              placeholder="Tìm theo tên quán"
              onSearch={onContractSearch}
              onChange={onContractSearchChange}
              value={contractSearch}
              enterButton
              allowClear
              style={{ width: 300 }}
            />
          </Row>
          <Table
            bordered
            loading={contractLoading}
            columns={contractColumns}
            dataSource={contractData}
            rowKey={(record) => record._id}
            pagination={contractPaginationWithTotal}
            onChange={onContractTableChange}
          />
        </div>
      ),
    },
  ];

  return (
    <Modal
      title={
        <Space>
          <FilterOutlined />
          Lọc quán
        </Space>
      }
      open={open}
      onCancel={onCancel}
      footer={[
        <Button key="close" onClick={onCancel}>
          Đóng
        </Button>,
      ]}
      width={1200}
      centered
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab} items={items} />
    </Modal>
  );
}

export default FilterRestaurantModal;
